import { useMemo, useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { useAdminApiClient } from '../api/AdminApiClientContext'
import { queryKeys, type AuditKey } from '../api/queryKeys'
import type {
  AuditEvent,
  AuditListResponse,
  AuditTargetType,
} from '../api/types'
import { CodeChip } from '../components/CodeChip'
import {
  DataTable, type ColumnConfig,
} from '../components/DataTable'
import { PaginationControl } from '../components/PaginationControl'
import { RelativeTime } from '../components/RelativeTime'
import { useCursorPaginatedQuery } from '../hooks/useCursorPaginatedQuery'

// Backend query parameters per `helloworld/main.py::admin_audit`.
// We mirror them 1:1 in the URL so an operator can copy a filtered
// view link to a colleague.
const PARAM_KEYS = [
  'operator_id', 'target_type', 'target_id', 'action', 'since', 'until',
] as const
type ParamKey = typeof PARAM_KEYS[number]

const TARGET_TYPES: AuditTargetType[] = [
  'OPERATOR', 'USER', 'DELIVERY', 'SYSTEM',
]

interface FilterState {
  operator_id: string
  target_type: string
  target_id: string
  action: string
  since: string
  until: string
}

function readFilters(params: URLSearchParams): FilterState {
  const out = {} as FilterState
  for (const key of PARAM_KEYS) {
    out[key] = params.get(key) ?? ''
  }
  return out
}

function buildAuditKey(filters: FilterState): AuditKey {
  const key: AuditKey = {}
  if (filters.operator_id) key.operatorId = filters.operator_id
  if (filters.target_type) key.targetType = filters.target_type
  if (filters.target_id)   key.targetId   = filters.target_id
  if (filters.action)      key.action     = filters.action
  if (filters.since)       key.since      = Number(filters.since)
  if (filters.until)       key.until      = Number(filters.until)
  return key
}

function buildQueryString(filters: FilterState, cursor: string): string {
  const search = new URLSearchParams({ limit: '50' })
  for (const key of PARAM_KEYS) {
    const value = filters[key]
    if (value) search.set(key, value)
  }
  if (cursor) search.set('cursor', cursor)
  return search.toString()
}

function targetLink(event: AuditEvent): string | null {
  if (event.target_type === 'USER' && event.target_id) {
    return `/death-confirmation/${event.target_id}`
  }
  if (event.target_type === 'DELIVERY' && event.target_id) {
    return `/deliveries/${event.target_id}`
  }
  return null
}

export function AuditLogPage() {
  const client = useAdminApiClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const filters = useMemo(() => readFilters(searchParams), [searchParams])

  const result = useCursorPaginatedQuery<AuditEvent>({
    queryKey: queryKeys.audit(buildAuditKey(filters)),
    fetchPage: (cursor) => client.get<AuditListResponse>(
      `/r/admin/audit?${buildQueryString(filters, cursor)}`,
    ),
  })

  function applyFilters(next: FilterState) {
    const search = new URLSearchParams()
    for (const key of PARAM_KEYS) {
      if (next[key]) search.set(key, next[key])
    }
    setSearchParams(search, { replace: true })
  }

  const columns: ColumnConfig<AuditEvent>[] = [
    {
      key: 'time',
      header: 'When',
      cell: (event) => <RelativeTime timestamp={event.time} />,
    },
    {
      key: 'operator',
      header: 'Operator',
      cell: (event) => (
        <span className="text-stone-700">
          {event.operator_email_at_time}
          <span className="ml-1 text-xs text-stone-400">
            {event.operator_id}
          </span>
        </span>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      cell: (event) => <CodeChip>{event.action}</CodeChip>,
    },
    {
      key: 'target',
      header: 'Target',
      cell: (event) => {
        const link = targetLink(event)
        const inner = (
          <span>
            <CodeChip>{event.target_type}</CodeChip>
            {event.target_id && (
              <span className="ml-2">
                <CodeChip>{event.target_id}</CodeChip>
              </span>
            )}
          </span>
        )
        return link
          ? <Link to={link} className="text-accent hover:underline">{inner}</Link>
          : inner
      },
    },
    {
      key: 'outcome',
      header: 'Outcome',
      cell: (event) => (
        <span
          className={[
            'rounded-full px-2 py-0.5 text-xs font-medium',
            event.outcome === 'SUCCESS'
              ? 'bg-emerald-100 text-emerald-800'
              : 'bg-red-100 text-red-800',
          ].join(' ')}
        >
          {event.outcome}
          {event.error_code && (
            <span className="ml-1 font-mono text-[10px] text-red-700">
              {event.error_code}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'reason',
      header: 'Reason / notes',
      cell: (event) => event.reason
        ? <span className="whitespace-pre-wrap text-stone-700">
            {event.reason}
          </span>
        : <span className="text-xs text-stone-400">—</span>,
    },
  ]

  return (
    <div className="space-y-4 p-6">
      <header className="flex items-baseline justify-between">
        <h1 className="text-xl font-semibold text-stone-900">Audit log</h1>
        <span className="text-xs text-stone-500">
          Operator and system actions, newest first.
        </span>
      </header>

      <FilterToolbar filters={filters} onApply={applyFilters} />

      <DataTable<AuditEvent>
        caption="Operator audit log"
        columns={columns}
        rows={result.flatItems}
        rowKey={(row) => row.event_id}
        isLoading={result.isLoading}
        isError={result.isError}
        emptyTitle="No audit events match this filter"
        emptyDescription="Adjust the filter or wait for the next operator action."
        footer={
          <PaginationControl
            loadedCount={result.flatItems.length}
            hasMore={result.hasMore}
            isFetchingMore={result.isFetchingMore}
            onLoadMore={() => result.loadMore()}
          />
        }
      />
    </div>
  )
}

interface FilterToolbarProps {
  filters: FilterState
  onApply: (filters: FilterState) => void
}

function FilterToolbar({ filters, onApply }: FilterToolbarProps) {
  // Local draft state so the operator can compose a filter without
  // refetching on every keystroke. URL is updated only on Apply/Reset.
  const [draft, setDraft] = useState<FilterState>(filters)
  // Re-sync the draft if the URL changes (e.g. browser back/forward).
  useEffect(() => { setDraft(filters) }, [filters])

  function patch(key: ParamKey, value: string) {
    setDraft({ ...draft, [key]: value })
  }
  function apply(event: React.FormEvent) {
    event.preventDefault()
    onApply(draft)
  }
  function reset() {
    const empty: FilterState = {
      operator_id: '', target_type: '', target_id: '',
      action: '', since: '', until: '',
    }
    setDraft(empty)
    onApply(empty)
  }

  return (
    <form
      onSubmit={apply}
      className="flex flex-wrap items-end gap-2 rounded-lg border
                 border-stone-200 bg-white p-3 shadow-sm"
    >
      <FilterInput
        label="Operator id"
        value={draft.operator_id}
        onChange={(v) => patch('operator_id', v)}
      />
      <FilterSelect
        label="Target type"
        value={draft.target_type}
        onChange={(v) => patch('target_type', v)}
        options={['', ...TARGET_TYPES]}
      />
      <FilterInput
        label="Target id"
        value={draft.target_id}
        onChange={(v) => patch('target_id', v)}
      />
      <FilterInput
        label="Action"
        value={draft.action}
        onChange={(v) => patch('action', v)}
      />
      <FilterInput
        label="Since (unix s)"
        value={draft.since}
        onChange={(v) => patch('since', v)}
        inputMode="numeric"
      />
      <FilterInput
        label="Until (unix s)"
        value={draft.until}
        onChange={(v) => patch('until', v)}
        inputMode="numeric"
      />
      <div className="ml-auto flex gap-2">
        <button
          type="submit"
          className="rounded-md bg-accent px-3 py-1 text-xs font-semibold
                     text-white shadow-sm hover:bg-accent/90"
        >
          Apply
        </button>
        <button
          type="button"
          onClick={reset}
          className="rounded-md border border-stone-200 bg-white px-3 py-1
                     text-xs font-medium text-stone-700 hover:bg-stone-50"
        >
          Reset
        </button>
      </div>
    </form>
  )
}

interface FilterInputProps {
  label: string
  value: string
  onChange: (value: string) => void
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
}

function FilterInput({ label, value, onChange, inputMode }: FilterInputProps) {
  return (
    <label className="flex flex-col gap-1 text-xs text-stone-600">
      <span>{label}</span>
      <input
        type="text"
        inputMode={inputMode}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="rounded-md border border-stone-300 px-2 py-1 text-sm
                   focus:border-accent focus:outline-none"
      />
    </label>
  )
}

interface FilterSelectProps {
  label: string
  value: string
  onChange: (value: string) => void
  options: string[]
}

function FilterSelect({ label, value, onChange, options }: FilterSelectProps) {
  return (
    <label className="flex flex-col gap-1 text-xs text-stone-600">
      <span>{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="rounded-md border border-stone-300 px-2 py-1 text-sm
                   focus:border-accent focus:outline-none"
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option || 'Any'}
          </option>
        ))}
      </select>
    </label>
  )
}
